import { useCallback, useEffect, useRef, useState } from 'react'
import API from '../../../../services/API'

export function useControlRoomSnapshot(controlRoomID, intervalMs = 2000) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [syncState, setSyncState] = useState('connecting')
  const [lastUpdated, setLastUpdated] = useState(null)

  const versionRef = useRef(null)
  const lastContactRef = useRef(null)
  const failedPollsRef = useRef(0)

  const fetchSnapshot = useCallback(async () => {
    if (!controlRoomID) return

    try {
      const res = await API.get(`/controlrooms/${controlRoomID}/snapshot`, {
        headers: versionRef.current
          ? {
              'If-None-Match': versionRef.current,
            }
          : {},
        validateStatus: (status) => status === 200 || status === 304,
      })
      failedPollsRef.current = 0
      lastContactRef.current = new Date()
      setSyncState('live')
      if (res.status === 304) {
        return
      }
      const body = res.data
      // Store the server's actual ETag (it carries more than the room version
      // — e.g. a time bucket for shift-driven data), falling back to the
      // payload version for older responses.
      versionRef.current =
        res.headers?.etag || (body?.version ? `"${body.version}"` : null)
      setLastUpdated(lastContactRef.current)
      setData(body)
    } catch (err) {
      failedPollsRef.current += 1

      if (!navigator.onLine || failedPollsRef.current >= 3) {
        setSyncState('offline')
      } else {
        setSyncState('delayed')
      }

      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [controlRoomID])

  useEffect(() => {
    if (!controlRoomID) return

    setLoading(true)
    setSyncState('connecting')
    setLastUpdated(null)
    failedPollsRef.current = 0
    lastContactRef.current = null
    versionRef.current = null

    fetchSnapshot()

    const interval = setInterval(fetchSnapshot, intervalMs)

    return () => clearInterval(interval)
  }, [controlRoomID, intervalMs, fetchSnapshot])

  useEffect(() => {
    if (syncState === 'offline') return

    const timer = setInterval(() => {
      if (!lastContactRef.current) return

      const secondsSinceSync =
        (Date.now() - new Date(lastContactRef.current).getTime()) / 1000

      if (secondsSinceSync > 10) {
        setSyncState('delayed')
      }
    }, 1000)

    return () => clearInterval(timer)
  }, [syncState])

  useEffect(() => {
    const online = () => {
      setSyncState('connecting')
      fetchSnapshot()
    }

    const offline = () => setSyncState('offline')

    window.addEventListener('online', online)
    window.addEventListener('offline', offline)

    return () => {
      window.removeEventListener('online', online)
      window.removeEventListener('offline', offline)
    }
  }, [fetchSnapshot])

  return {
    data,
    loading,
    syncState,
    lastUpdated,
    refresh: fetchSnapshot,
  }
}
